import { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { validationMessages } from 'constants/validation'
import {
	Stack, StackProps,	
	Rating,
	TextField,
	Box,
	CircularProgress,
	Button,
	Collapse,
	Paper,
	List,
	ListItem,
	Typography,
	Select,
	MenuItem
} from '@mui/material'

import { Section } from 'components/controls/section'

import { SearchsDiseaseFormData } from 'interface/search/disease'
import { SearchICD10Diseases, DiseaseResult } from 'interface/icd10'
import { ErrorInfo } from 'components/controls/errorInfo'

const rateList = [
  { rate: 0.4, value: 1, description: '星1' },
  { rate: 0.8, value: 2, description: '星2' },
  { rate: 1.0, value: 3, description: '星3' },
  { rate: 1.2, value: 4, description: '星4' },
  { rate: 1.6, value: 5, description: '星5' },
]

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
  diseaseName: yup.string().required(Required),
  searchWord: yup.string(),
  searchWordJpn: yup.string(),
  icd10code: yup.string(),
  dpcCode: yup.string(),
  dpcYear: yup.number(),
  cancerCode: yup.string(),
  cancerYear: yup.number(),
	journalOption: yup.string(),
  isTrialWord: yup.boolean(),
  rate: yup.number()
})

type useSearchsDiseaseFormProps = {
	data?: SearchsDiseaseFormData
	onSubmit: (data: SearchsDiseaseFormData) => void
}
export const useSearchsDiseaseForm = ({
	data,
	onSubmit
}: useSearchsDiseaseFormProps) => {
	const form = useForm({
		defaultValues: data ? {
			diseaseName: data.diseaseName ?? '',
			searchWord: data.searchWord ?? '',
			searchWordJpn: data.searchWord ?? '',
			icd10code: data.icd10code ?? '',
			dpcCode: data.dpcCode ?? '',
			dpcYear: data.dpcYear ?? 0,
			cancerCode: data.cancerCode ?? '',
			cancerYear: data.cancerYear ?? 0,
			journalOption: data.journalOption ?? '',
			isTrialWord: data.isTrialWord ?? false,
			rate: data.rate ?? 1
		} : {
			diseaseName: '',
			searchWord: '',
			searchWordJpn: '',
			icd10code: '',
			dpcCode: '',
			dpcYear: 0,
			cancerCode: '',
			cancerYear: 0,
			journalOption: '',
			isTrialWord: false,
			rate: 1
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		submit: form.handleSubmit(input => onSubmit({
			diseaseName: input.diseaseName,
			searchWord: input.searchWord ?? '',
			searchWordJpn: input.searchWordJpn ?? '',
			icd10code: input.icd10code ?? '',
			dpcCode: input.dpcCode ?? '',
			dpcYear: input.dpcYear === 0 ? null : input.dpcYear,
			cancerCode: input.cancerCode ?? '',
			cancerYear: input.cancerYear === 0 ? null : input.cancerYear,
			journalOption: input.journalOption ?? '',
			isTrialWord: input.isTrialWord ?? false,
			rate: input.rate ?? 1,
			updateTime: data?.updateTime
		}))
	}
}

export const SearchsDiseaseForm = ({
	form: {
		control,
		setValue,
		formState: { errors },
		watch,
	},
	...others
}: {
	form: ReturnType<typeof useSearchsDiseaseForm>
} & StackProps) => {
	const [isSearching, setIsSearching] = useState<boolean>(false)
	const [searchDiseaseError, setSearchDiseaseError] = useState<Error | null>(null)
	const [searchDiseaseResult, setSearchDiseaseResult] = useState<DiseaseResult[]>([])

	const searchWord = watch(["diseaseName", "searchWord", "searchWordJpn"])
	const joinedSearchWord = useMemo(
		() => searchWord.flatMap(t => {
			const trimed = t?.trim()
			return (trimed && trimed !== '') ? trimed : []
		}).join('+'),
		[searchWord]
	)
	const searchDisease = useCallback(
		() => {
			if (isSearching) return
			setSearchDiseaseError(null)
			if (joinedSearchWord === '') return
			setIsSearching(true)
			SearchICD10Diseases(joinedSearchWord, { limit: 10 })
				.catch((error) => setSearchDiseaseError(error))
				.then((result) => setSearchDiseaseResult(result?.list ?? []))
				.finally(() => setIsSearching(false))
		},
		[isSearching, setIsSearching, setSearchDiseaseError, setSearchDiseaseResult, joinedSearchWord]
	)
	const chooseDisease = useCallback(
		(disease: DiseaseResult) => {
			setValue("searchWordJpn", disease.diseaseName)
			if (disease.diseaseNameEnglish) setValue("searchWord", disease.diseaseNameEnglish)
			if (disease.icd10code) setValue("icd10code", disease.icd10code)
			if (disease.dpc?.code) {
				setValue("dpcCode", disease.dpc?.code)
				setValue("dpcYear", 2021)
			}
			setSearchDiseaseResult([])
		},
		[setValue, setSearchDiseaseResult]
	)
	return (
		<Stack direction="column" {...others}>
			<Section label="疾患名" labelProps={{ paddingY: 2 }}>
				<Controller
					control={control}
					name="diseaseName"
					render={({ field }) => (
						<>
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.diseaseName}
								helperText={errors.diseaseName ? errors.diseaseName.message : ''}
							/>
						</>
					)}
				/>
			</Section>

			<Section label="検索語（英語）" labelProps={{ paddingY: 2 }}>
				<Controller
					control={control}
					name="searchWord"
					render={({ field }) => (
						<>
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.searchWord}
								helperText={errors.searchWord ? errors.searchWord.message : ''}
							/>
						</>
					)}
				/>
			</Section>
			
			<Section label="検索語（日本語）" labelProps={{ paddingY: 2 }}>
				<Controller
					control={control}
					name="searchWordJpn"
					render={({ field }) => (
						<>
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.searchWordJpn}
								helperText={errors.searchWordJpn ? errors.searchWordJpn.message : ''}
							/>
						</>
					)}
				/>
			</Section>

			<Box padding={1} position="relative">
				{isSearching && (
					<CircularProgress
						size={24}
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							marginTop: '-12px',
							marginLeft: '-12px',
						}}
					/>
				)}
				<Button
					fullWidth
					variant="outlined"
					size="small"
					disabled={isSearching || joinedSearchWord === ''}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={searchDisease}
				>
					疾患検索
				</Button>

				<Collapse
					in={searchDiseaseResult.length > 0}
					timeout="auto"
					unmountOnExit
				>
					{searchDiseaseError && <ErrorInfo error={searchDiseaseError} />}

					<Paper sx={{ paddingX: 2, paddingY: 1, marginTop: 1 }}>
						<List disablePadding>
							{searchDiseaseResult.map((disease, i) => (
								<ListItem disablePadding key={`search-result-${i}`}>
									<Stack
										direction="row"
										spacing={2}
										width="100%"
										alignItems="center"
									>
										<Typography variant="body1" flexGrow={1}>
											{disease.icd10code} : {disease.diseaseName}
										</Typography>
										{disease.dpc && (
											<Typography variant="body1">
												{disease.dpc.code} : {disease.dpc.name}
											</Typography>
										)}
										<Button
											variant="text"
											size="small"
											sx={{ whiteSpace: 'nowrap' }}
											onClick={() => chooseDisease(disease)}
										>
											選択
										</Button>
									</Stack>
								</ListItem>
							))}
						</List>
					</Paper>
				</Collapse>
			</Box>

			<Section label="ICD10" labelProps={{ paddingY: 2 }}>
				<Controller
					control={control}
					name="icd10code"
					render={({ field }) => (
						<>
							<TextField
								{...field}
								variant="outlined"
								size="small"
								sx={{ width: '6rem' }}
								error={!!errors.icd10code}
								helperText={errors.icd10code ? errors.icd10code.message : ''}
							/>
						</>
					)}
				/>
			</Section>

			<Section label="DPC" labelProps={{ paddingY: 2 }}>
				<Stack direction="row" spacing={1}>
					<Controller
						control={control}
						name="dpcCode"
						render={({ field }) => (
							<>
								<TextField
									{...field}
									variant="outlined"
									size="small"
									sx={{ width: '6rem' }}
									error={!!errors.dpcCode}
									helperText={errors.dpcCode ? errors.dpcCode.message : ''}
								/>
							</>
						)}
					/>
					<Controller
						control={control}
						name="dpcYear"
						render={({ field }) => (
							<Select
								{...field}
								variant="outlined"
								size="small"
							>
								<MenuItem value={0}>無効</MenuItem>
								<MenuItem value={2021}>2021年度</MenuItem>
							</Select>
						)}
					/>
				</Stack>
			</Section>

			<Section label="がん情報" labelProps={{ paddingY: 2 }}>
				<Stack direction="row" spacing={1}>
					<Controller
						control={control}
						name="cancerCode"
						render={({ field }) => (
							<>
								<TextField
									{...field}
									variant="outlined"
									size="small"
									sx={{ width: '6rem' }}
									error={!!errors.dpcCode}
									helperText={errors.dpcCode ? errors.dpcCode.message : ''}
								/>
							</>
						)}
					/>
					<Controller
						control={control}
						name="cancerYear"
						render={({ field }) => (
							<Select
								{...field}
								variant="outlined"
								size="small"
							>
								<MenuItem value={0}>無効</MenuItem>
								<MenuItem value={2021}>2021年度</MenuItem>
							</Select>
						)}
					/>
				</Stack>
			</Section>

			<Section label="重要度" labelProps={{ paddingY: 2 }}>
				<Controller
					control={control}
					name="rate"
					render={({ field }) => (
						<Rating
							value={rateList.find(r => r.rate === field.value)?.value ?? 0}
							name="rate"
							onChange={(e, v) => field.onChange(e, rateList.find(r => r.value === v)?.rate ?? 10)}
						/>
					)}
				/>
			</Section>
		</Stack>
	)
}